import { ReactNode, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CollectionLiabilityEntry, getCollectionLiabilityDetails } from '../../../api/collectionLiabilitiesApi';
import { ArrowLeftIcon } from '../../../components/icons';
import { formatLongDate } from '../../../utils/calendar';
import { getStatusColors, STATUS_LABEL_KEYS } from './liabilityStatus';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function CollectionDetailsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [liability, setLiability] = useState<CollectionLiabilityEntry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getCollectionLiabilityDetails(id)
      .then(setLiability)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!liability) {
    return (
      <Box sx={(theme) => ({ p: 4, width: '100%', flex: 1, backgroundColor: theme.app.mutedBackground })}>
        <Typography variant="body1">{t('collectionDetails.notFound')}</Typography>
      </Box>
    );
  }

  const { collection, payer, status } = liability;
  const childLabel = payer.child ? `${payer.child.firstName} ${payer.child.lastName}` : '-';

  return (
    <Box sx={(theme) => ({ p: 4, width: '100%', flex: 1, backgroundColor: theme.app.mutedBackground })}>
      <Box
        onClick={() => navigate('/parent/collections')}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          cursor: 'pointer',
          color: theme.app.mutedForeground,
          width: 'fit-content',
        })}
      >
        <ArrowLeftIcon size={16} />
        <Typography variant="body2">{t('collectionDetails.back')}</Typography>
      </Box>

      <Typography variant="h4" sx={{ mb: 3 }}>
        {collection.name}
      </Typography>

      <Box
        sx={(theme) => ({
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.app.border}`,
          backgroundColor: theme.app.card,
          display: 'flex',
          flexDirection: 'column',
          // maxWidth: 640,
        })}
      >
        <Box sx={(theme) => ({ display: 'flex', gap: 4, pb: 2.5, borderBottom: `1px solid ${theme.app.border}` })}>
          <Field label={t('collectionDetails.issueDate')} value={formatLongDate(collection.createdAt)} />
          <Field label={t('collectionDetails.dueDate')} value={formatLongDate(collection.deadline)} />
        </Box>
        <Box sx={(theme) => ({ display: 'flex', gap: 4, py: 2.5, borderBottom: `1px solid ${theme.app.border}` })}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('collectionDetails.paymentStatus')}
            </Typography>
            <Box
              sx={(theme) => {
                const colors = getStatusColors(theme, status);
                return { display: 'inline-block', px: 1.5, py: 0.5, borderRadius: 99, backgroundColor: colors.bg, color: colors.color };
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {t(STATUS_LABEL_KEYS[status])}
              </Typography>
            </Box>
          </Box>
          <Field label={t('collectionDetails.organization')} value={collection.organization?.name} />
        </Box>
        <Box sx={(theme) => ({ display: 'flex', gap: 4, py: 2.5, borderBottom: `1px solid ${theme.app.border}` })}>
          <Field label={t('collectionDetails.child')} value={childLabel} />
          <Box sx={{ flex: 1 }} />
        </Box>
        <Box sx={{ pt: 2.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('collectionDetails.description')}
          </Typography>
          <Typography variant="body2">{collection.description}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
